import { PendingTimesheet } from '../models/pending-timesheets.model';
import dayjs from 'dayjs';
import { AppConfig } from 'features/config/config.model';
import { IOnlineApprovalTimeEntry } from '../models/online-approval-time-entry.model';
import { TimeEntryLogic } from '@ms/timesheets/dist/logic/time-entry.logic';

export interface IMissingValues {
    timeIn: boolean;
    timeOut: boolean;
    lunchIn: boolean;
    lunchOut: boolean;
}

export class TimesheetLogic {
    static removePastApprovedTimesheets(timesheets: PendingTimesheet[]): PendingTimesheet[] {
        let filteredTimesheets = timesheets
            .sort((a: PendingTimesheet, b: PendingTimesheet) => {
                return dayjs(a.payPeriodEnd).diff(b.payPeriodEnd);
            })
            .reverse();

        let payPeriodStart = null;

        if (filteredTimesheets.length) {
            payPeriodStart = filteredTimesheets[0].payPeriodStart;
        }

        if (payPeriodStart) {
            filteredTimesheets = filteredTimesheets.filter((timesheet: PendingTimesheet) => {
                return !timesheet.onlineApprovalDate || dayjs(timesheet.onlineApprovalDate).isAfter(payPeriodStart);
            });
        }

        return filteredTimesheets;
    }

    static showAddDelete(onlineApprovalDate: Date, config: AppConfig): boolean {
        return config?.SHOW_ADD_DELETE && !onlineApprovalDate;
    }

    static createDeleteMsg(updatedTimeEntry: IOnlineApprovalTimeEntry): string {
        const formattedDate = dayjs(updatedTimeEntry.entryDate).format('MM/DD/YYYY');
        const formattedHours = TimeEntryLogic.calcHours(updatedTimeEntry)?.toFixed(2);

        return `${formattedHours} Hour${formattedHours === '1' ? '' : 's'} removed for ${formattedDate} `;
    }

    static hasAnyMissingTimes(
        timeEntry: IOnlineApprovalTimeEntry,
    ): {
        hasAnyMissingValues: boolean;
        missingValues: IMissingValues;
    } {
        let hasAnyMissingValues = false;

        const missingValues = {
            timeOut: false,
            timeIn: false,
            lunchOut: false,
            lunchIn: false,
            shiftType: false,
        };

        if (timeEntry.timeIn && !timeEntry.timeOut) {
            missingValues.timeOut = true;
            hasAnyMissingValues = true;
        }

        if (timeEntry.timeOut && !timeEntry.timeIn) {
            missingValues.timeIn = true;
            hasAnyMissingValues = true;
        }

        if (timeEntry.lunchIn && !timeEntry.lunchOut) {
            missingValues.lunchOut = true;
            hasAnyMissingValues = true;
        }

        if (timeEntry.lunchOut && !timeEntry.lunchIn) {
            missingValues.lunchIn = true;
            hasAnyMissingValues = true;
        }

        return { hasAnyMissingValues, missingValues };
    }

    static hasChanges(
        timeEntries: IOnlineApprovalTimeEntry[],
        selectedTimesheet: PendingTimesheet,
        supervisorComments: string,
    ): boolean {
        const changedRows = timeEntries.filter((entry) => {
            return entry.hasChanges && entry.isActive;
        }).length;

        const newlyAddedOrDeletedRows = timeEntries.filter((entry) => {
            return (entry.isActive && entry.isNew) || (!entry.isActive && !entry.isNew);
        }).length;

        return !(
            changedRows === 0 &&
            supervisorComments === selectedTimesheet?.supervisorComment &&
            newlyAddedOrDeletedRows === 0
        );
    }

    static commentIsEmpty(comment: string): boolean {
        return (comment || '').trim() === '';
    }

    static hasInvalidEntries(timeEntries: IOnlineApprovalTimeEntry[]): boolean {
        const invalidRows = timeEntries.filter((entry) => {
            return entry.isActive && entry.isInvalid;
        }).length;

        return invalidRows > 0;
    }

    static isAlreadyApproved(timesheet: PendingTimesheet): boolean {
        return !!timesheet?.onlineApprovalDate;
    }

    static calculateHasLunch(timeEntry: IOnlineApprovalTimeEntry): boolean {
        return !!(
            timeEntry.lunchIn &&
            timeEntry.lunchOut &&
            dayjs(timeEntry.lunchIn).isValid() &&
            dayjs(timeEntry.lunchOut).isValid()
        );
    }
}
